// src/components/SearchPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import EmptyState from './EmptyState';
import AnimatedSearchExample from './AnimatedSearchExample';
import GuidedSearchModal from './GuidedSearchModal';
import { parseNaturalLanguageQuery } from '../utils/naturalLanguageSearch';
import { searchGames } from '../api/games';
import { useAuth } from '../context/AuthContext';
import LoginModal from './auth/LoginModal';
import GameCard from './GameCard';
import { MECHANICS, THEMES } from '../utils/gameConstants';
import SEO from './SEO';


const SearchPage = ({ resetTrigger }) => {

  //login
  const { currentUser } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginCallback, setLoginCallback] = useState(null);
  const location = useLocation();

  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rawQuery, setRawQuery] = useState('');
  const [usingNaturalLanguage, setUsingNaturalLanguage] = useState(false);
  const [appliedNlFilters, setAppliedNlFilters] = useState(null);
  const [filters, setFilters] = useState({
    players: '',
    maxTime: '',
    difficulty: '',
    themes: [],
    mechanics: []
  });
  const [games, setGames] = useState([]);
  const [displayedGames, setDisplayedGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    theme: [],
    mechanic: []
  });
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const initialFetchDone = useRef(false);

  // New state for tracking filter changes
  const [filtersChanged, setFiltersChanged] = useState(false);
  // guided search
  const [isGuidedModalOpen, setIsGuidedModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const processedResetTriggerRef = useRef(-1);
  const searchTimeoutRef = useRef(null);
  const filtersRef = useRef(filters);
  const searchTermRef = useRef(searchTerm);
  const [processingSearch, setProcessingSearch] = useState(false);

  //URL querystring
  const [searchParams, setSearchParams] = useSearchParams();




  // Simplified handleNaturalLanguageSearch function with useCallback
  const handleNaturalLanguageSearch = useCallback((query) => {
    setRawQuery(query);
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

    // Initial mount effect to handle URL parameters on first load (prevent fouc)
    useEffect(() => {
      if (!initialFetchDone.current) {
        const urlParams = new URLSearchParams(location.search);
        console.log(location.search);
        const nlq = urlParams.get('nlq');
        const guided = urlParams.get('guided');
        
        if (nlq) {
          console.log("NLQ detected");
          // Set the raw query and mark as using natural language
          setRawQuery(nlq);
          //setUsingNaturalLanguage(true);
          //setSearchPerformed(true);
          //setShouldFetch(true);
          
          // Remove the nlq parameter from the URL
          urlParams.delete('nlq');
          const newSearch = urlParams.toString();
          const newUrl = `${window.location.origin}${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
          window.history.replaceState(null, document.title, newUrl);
        }
        
        if (guided) {
          console.log("Guided param detected");

          


          //setRawQuery(guided);
          //setUsingNaturalLanguage(true);
          //setSearchPerformed(true);
          //setShouldFetch(true);
          
          // Set up the filters based on URL parameters
          const newFilters = {...filters};
          let stateChanged = false;
          
          const players = urlParams.get('players');
          if (players) {
            newFilters.players = parseInt(players, 10);
            stateChanged = true;
          }
          
          const maxTime = urlParams.get('time');
          if (maxTime) {
            newFilters.maxTime = maxTime;
            stateChanged = true;
          }
          
          const themes = urlParams.get('themes');
          if (themes) {
            newFilters.themes = themes.split(',');
            stateChanged = true;
          }
          
          if (stateChanged) {
            setFilters(newFilters);
            setSearchPerformed(true);
            setShouldFetch(true);

          // Fix the URL
          urlParams.delete('guided');
          const newSearch = urlParams.toString();
          const newUrl = `${window.location.origin}${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
          window.history.replaceState(null, document.title, newUrl);
          initialFetchDone.current = true;
        }
      }

        //initialFetchDone.current = true;
      }
    }, [location.search, filters]);

  // Process rawQuery changes - for searches passed from the homepage
  useEffect(() => {
    // Skip if rawQuery is not set or empty
    if (!rawQuery || rawQuery.trim() === '') return;


    // Set processing flag to prevent other effects from triggering searches
    setProcessingSearch(true);
    
    console.log('Processing raw query:', rawQuery);
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    
    
    searchTimeoutRef.current = setTimeout(() => {
      // Parse the query
      const parsedQuery = parseNaturalLanguageQuery(rawQuery);
      console.log('Parsed query:', parsedQuery);
      
      // Update search term
      setSearchTerm(parsedQuery.searchTerm || '');
      
      // Store information about applied filters
      const appliedFilters = {};
      
      // Update filters using functional update to avoid dependency on current filters
      setFilters(prevFilters => {
        const newFilters = { ...prevFilters };
        
        // Set to empty arrays to avoid duplicate entries
        newFilters.themes = [];
        newFilters.mechanics = [];
        
        // Process players
        if (parsedQuery.players) {
          newFilters.players = parsedQuery.players;
          appliedFilters.players = parsedQuery.players;
        }
        
        // Process maxTime
        if (parsedQuery.maxTime) {
          newFilters.maxTime = parsedQuery.maxTime;
          appliedFilters.maxTime = parsedQuery.maxTime;
        }
        
        // Process weight/difficulty
        if (parsedQuery.weight) {
          let difficulty = '';
          if (['Light', 'Medium Light'].includes(parsedQuery.weight)) {
            difficulty = 'Easy';
          } else if (parsedQuery.weight === 'Medium') {
            difficulty = 'Medium';
          } else if (['Medium Heavy', 'Heavy'].includes(parsedQuery.weight)) {
            difficulty = 'Hard';
          }
          
          newFilters.difficulty = difficulty;
          appliedFilters.difficulty = difficulty;
        }
        
        // Process themes
        if (parsedQuery.themes && parsedQuery.themes.length > 0) {
          newFilters.themes = parsedQuery.themes;
          appliedFilters.themes = parsedQuery.themes;
        }
        
        // Process mechanics
        if (parsedQuery.mechanics && parsedQuery.mechanics.length > 0) {
          newFilters.mechanics = parsedQuery.mechanics;
          appliedFilters.mechanics = parsedQuery.mechanics;
        }
        
        return newFilters;
      });
      
      setAppliedNlFilters(appliedFilters);
      setUsingNaturalLanguage(true);
      setSearchPerformed(true);

      const queryParams = new URLSearchParams();
      
      // Use q parameter for search term
      if (parsedQuery.searchTerm) {
        queryParams.set('q', parsedQuery.searchTerm);
      }
      
      // Add other parameters
      if (parsedQuery.players) queryParams.set('players', parsedQuery.players.toString());
      if (parsedQuery.maxTime) queryParams.set('time', parsedQuery.maxTime.toString());
      if (appliedFilters.difficulty) queryParams.set('diff', appliedFilters.difficulty);
      if (parsedQuery.themes?.length > 0) queryParams.set('themes', parsedQuery.themes.join(','));
      if (parsedQuery.mechanics?.length > 0) queryParams.set('mechs', parsedQuery.mechanics.join(','));
      
      // Update URL - use replace to avoid history stacking
      setSearchParams(queryParams, { replace: true });
      
      
      // Now that we've processed everything, trigger a single search - but only on initial load
      if (!initialFetchDone.current) {
        setShouldFetch(true);
        initialFetchDone.current = true;
      }
      
      // Clear the processing flag after scheduling the search
      setProcessingSearch(false);
    }, 300); // 300ms debounce to prevent rapid processing
    
    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      setProcessingSearch(false);
    };
  }, [rawQuery, location.search, setSearchParams]);

  // Reset function with useCallback
  const resetFilters = useCallback(() => {
    setFilters({
      players: '',
      maxTime: '',
      difficulty: '',
      themes: [],
      mechanics: []
    });

    setSearchTerm('');
    setRawQuery('');
    setUsingNaturalLanguage(false);
    setAppliedNlFilters(null);
    
    // Clear URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('q');
    newSearchParams.delete('players');
    newSearchParams.delete('time');
    newSearchParams.delete('diff');
    newSearchParams.delete('themes');
    newSearchParams.delete('mechs');
    setSearchParams(newSearchParams);
    
    if (searchPerformed) {
      setGames([]);
      setDisplayedGames([]);
      setSearchPerformed(false);
      
    }
  }, [setFilters, setSearchTerm, setRawQuery, setUsingNaturalLanguage, setAppliedNlFilters, searchPerformed, searchParams, setSearchParams, setGames, setDisplayedGames, setSearchPerformed]);

  // Handle resetSearchState from game details page
  useEffect(() => {
    if (location.state) {
      // Case 1: Coming back from game details page with preserved search
      if (location.state.from === 'game-details' && location.state.preserveSearch) {
        // If we have saved results, restore them
        if (location.state.savedResults && Array.isArray(location.state.savedResults)) {
          console.log('Restoring saved search results');
          setGames(location.state.savedResults);
          setDisplayedGames(location.state.savedResults);
          setSearchPerformed(true);
          
          // Also restore search term from URL if available
          if (searchParams.get('q')) {
            setSearchTerm(searchParams.get('q'));
            setRawQuery(searchParams.get('q'));
            if (!usingNaturalLanguage) {
              setUsingNaturalLanguage(true);
            }
          }
          
          // Don't trigger a new fetch
          setShouldFetch(false);
        } 
        // If no saved results but URL params exist, restore the search
        else if (location.state.search) {
          // Restore search parameters if they were saved
          const searchQueryParams = new URLSearchParams(location.state.search);
          // Process search params as needed
          if (searchQueryParams.get('q')) {
            console.log('Restoring search from URL parameters');
            handleNaturalLanguageSearch(searchQueryParams.get('q'));
            setShouldFetch(true);
          }
        }
        // Clear the state property to prevent infinite loop
        window.history.replaceState({}, document.title);
      } 
      // Case 2: Old reset search state behavior
      else if (location.state.resetSearchState) {
        // Clear the state property to prevent infinite loop
        window.history.replaceState({}, document.title);
        
        // Reset the search state but keep existing URL params
        if (searchParams.get('q')) {
          handleNaturalLanguageSearch(searchParams.get('q'));
          setShouldFetch(true);
        }
      }
    }
  }, [location.state, searchParams, handleNaturalLanguageSearch, setGames, setDisplayedGames, setSearchPerformed, setShouldFetch, usingNaturalLanguage]);

  // Update refs when filters or searchTerm change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Then modify the URL params effect
  useEffect(() => {
    if (processingSearch || !initialFetchDone.current) return;

    const q = searchParams.get('q');
    const players = searchParams.get('players');
    const maxTime = searchParams.get('time');
    const difficulty = searchParams.get('diff');
    const themes = searchParams.get('themes');
    const mechanics = searchParams.get('mechs');
    
    // Create a new object for tracking changes
    const newFilters = {...filtersRef.current};
    let stateChanged = false;
    
    if (q && q !== searchTermRef.current) {
      setSearchTerm(q);
      setRawQuery(q);
      stateChanged = true;
    }
    
    if (players) {
      const playerValue = parseInt(players, 10);
      if (newFilters.players !== playerValue) {
        newFilters.players = playerValue;
        stateChanged = true;
      }
    }
    
    if (maxTime && newFilters.maxTime !== maxTime) {
      newFilters.maxTime = maxTime;
      stateChanged = true;
    }
    
    if (difficulty && newFilters.difficulty !== difficulty) {
      newFilters.difficulty = difficulty;
      stateChanged = true;
    }
    
    if (themes) {
      const themeArray = themes.split(',');
      // Check if themes array is different
      const themesChanged = !areArraysEqual(themeArray, newFilters.themes);
      if (themesChanged) {
        newFilters.themes = themeArray;
        stateChanged = true;
      }
    }
    
    if (mechanics) {
      const mechArray = mechanics.split(',');
      // Check if mechanics array is different
      const mechanicsChanged = !areArraysEqual(mechArray, newFilters.mechanics);
      if (mechanicsChanged) {
        newFilters.mechanics = mechArray;
        stateChanged = true;
      }
    }
    
    if (stateChanged) {
      setFilters(newFilters);
      setSearchPerformed(true);
      setShouldFetch(true);
    }
  }, [searchParams, setSearchTerm, setRawQuery, setFilters, setSearchPerformed, setShouldFetch, processingSearch, rawQuery]);


  const areArraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every(item => b.includes(item));
  };


  useEffect(() => {
    // Only process the reset if this trigger value hasn't been processed before
    if (resetTrigger !== undefined && 
        resetTrigger !== null && 
        resetTrigger > 0 && 
        processedResetTriggerRef.current !== resetTrigger) {
      
      console.log("Reset trigger explicitly changed, resetting filters");
      resetFilters();
      
      // Mark this trigger value as processed
      processedResetTriggerRef.current = resetTrigger;
    }
  }, [resetTrigger, resetFilters]);

  // Add global access to displayed games for state preservation
  useEffect(() => {
    // Make displayedGames available globally for state preservation during navigation
    window.displayedGames = displayedGames;
    
    // Clean up on unmount
    return () => {
      delete window.displayedGames;
    };
  }, [displayedGames]);

  //login modal 

  const showLogin = (callback = null) => {
    console.log("Setting login callback:", callback); // Add debugging
    setLoginCallback(() => callback); // Use function setter to avoid closure issue
    setShowLoginModal(true);
  };

  const handleLoginSuccess = () => {
    console.log("Login success! Callback exists:", !!loginCallback); // Add debugging
    if (loginCallback && typeof loginCallback === 'function') {
      loginCallback();
    }
    setLoginCallback(null);
  };

  // voting buttons
  const handleVote = (gameId, voteValue, upvotes, downvotes) => {
    // Update the votes in the game cards
    setGames(prevGames => 
      prevGames.map(game => 
        game.id === gameId 
          ? { ...game, userVote: voteValue, upvotes, downvotes } 
          : game
      )
    );
    
    setDisplayedGames(prevGames => 
      prevGames.map(game => 
        game.id === gameId 
          ? { ...game, userVote: voteValue, upvotes, downvotes } 
          : game
      )
    );
  };


  const fetchFilteredGames = useCallback(async (page = 1) => {
  try {
    //console.log('FETCH FILTERED GAMES CALLED with filters:', JSON.stringify(filters));
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    
    // Define mapDifficultyToWeights inside useCallback
    const mapDifficultyToWeights = (difficulty) => {
      if (!difficulty) return [];
      
      switch(difficulty) {
        case 'Easy':
          return ['Light', 'Medium Light', 'Light Medium'];
        case 'Medium':
          return ['Medium'];
        case 'Hard':
          return ['Medium Heavy', 'Heavy Medium', 'Heavy'];
        default:
          return [];
      }
    };
    
    // Prepare search parameters
    let searchParams = {};
    
    // Add search term if present
    if (searchTerm) {
      searchParams.search = searchTerm;
    }
    
    // Add player count parameter
    if (filters.players) {
      searchParams.players = filters.players;
    }

    // Add other filters
    if (filters.maxTime) searchParams.maxTime = filters.maxTime;
    
    // Add weight filters based on difficulty
    if (filters.difficulty) {
      const weights = mapDifficultyToWeights(filters.difficulty);
      if (weights.length > 0) {
        searchParams.weight = weights;
      }
    }
    
    // Add themes and mechanics
    if (filters.themes && filters.themes.length > 0) searchParams.themes = filters.themes;
    if (filters.mechanics && filters.mechanics.length > 0) { searchParams.mechanics = filters.mechanics; }
    
    // Use natural language query if that's what the user provided
    if (usingNaturalLanguage && rawQuery) {
      searchParams.naturalQuery = rawQuery;
      // Don't add the searchTerm separately if we're using natural language to avoid dupes
      delete searchParams.search;
    }
    
    // Call the API
    console.log('about to call SearchGames');
    console.table(searchParams);
    console.log(JSON.stringify(searchParams, null, 2));
    console.log(typeof searchParams, searchParams);
    const results = await searchGames(searchParams, page);

    const queryParams = new URLSearchParams();
    // Always include searchTerm as q parameter if it exists
    if (searchTerm) {
      queryParams.set('q', searchTerm);
      console.log("Setting q parameter to:", searchTerm);
    }

    //only set searchTerm as a query param if it's NOT from natural language processing
    if (searchTerm && !usingNaturalLanguage) queryParams.set('q', searchTerm);
    // If using natural language and there's still a raw query, preserve it
    if (filters.players) queryParams.set('players', filters.players.toString());
    if (filters.maxTime) queryParams.set('time', filters.maxTime.toString());
    if (filters.difficulty) queryParams.set('diff', filters.difficulty);
    if (filters.themes.length > 0) queryParams.set('themes', filters.themes.join(','));
    if (filters.mechanics.length > 0) queryParams.set('mechs', filters.mechanics.join(','));
    setSearchParams(queryParams);
    
    // Store the fetched games
    let gamesData = results.games || results;
    
    // If we have games and a logged-in user, get the user's votes
    if (gamesData.length > 0 && currentUser) {
      // First get all game IDs
      const gameIds = gamesData.map(game => game.id);
      
      // Then fetch user votes for these games
      const userVotesPromises = gameIds.map(async (gameId) => {
        try {
          const response = await fetch(`/api/games/${gameId}/votes`, {
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            return { gameId, userVote: data.userVote };
          }
          return { gameId, userVote: 0 };
        } catch (err) {
          console.error(`Error fetching vote for game ${gameId}:`, err);
          return { gameId, userVote: 0 };
        }
      });
      
      const userVotes = await Promise.all(userVotesPromises);
      const userVoteMap = userVotes.reduce((map, vote) => {
        map[vote.gameId] = vote.userVote;
        return map;
      }, {});
      
      // Add user's votes to each game
      gamesData = gamesData.map(game => ({
        ...game,
        userVote: userVoteMap[game.id] || 0
      }));
    }
    
    setGames(gamesData);
    setDisplayedGames(gamesData);
    setTotalPages(results.totalPages || Math.ceil(results.total / 36) || 1);
    setLoading(false);
  } catch (err) {
    console.error('Error fetching games:', err);
    setError('Failed to fetch games. Please try again.');
    setLoading(false);
  }
}, [searchTerm, filters, usingNaturalLanguage, rawQuery, currentUser, setSearchParams]);

  // Load filter options only on initial mount
  useEffect(() => {
    // Simply set filter options directly from constants
    setFilterOptions({
      theme: THEMES,
      mechanic: MECHANICS
    });
    setFiltersLoaded(true);
  }, []);

  // Auto-fetch when filtersChanged is true
  useEffect(() => {
    if (filtersChanged && !processingSearch && !shouldFetch) {
      console.log('Fetching because filters changed and not already fetching');
      fetchFilteredGames();
      setFiltersChanged(false);
    }
  }, [filtersChanged, processingSearch, shouldFetch, fetchFilteredGames]);

  // Only fetch when shouldFetch is true
  useEffect(() => {
    if (shouldFetch) {
      console.log("FETCHING WITH FILTERS:", JSON.stringify(filters));
      const fetchGames = async () => {
        // Clear the flag first to prevent race conditions
        setShouldFetch(false);
        await fetchFilteredGames();
      };
      
      fetchGames();
    }
  }, [shouldFetch, fetchFilteredGames, filters]);

  // Check if a search should be performed when form is submitted
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setShouldFetch(true);
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.maxTime) count++;
    if (filters.difficulty) count++;
    if (filters.themes.length > 0) count++;
    if (filters.mechanics.length > 0) count++;
    if (filters.players !== 4) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Check if presets are active
  const isSoloPresetActive = filters.players === 1;
  const isPartyPresetActive = filters.players === 6 && filters.difficulty === 'Easy';
  const isKidsPresetActive = filters.themes.includes("Kids");
  const isQuickPresetActive = filters.maxTime === '30';
  const isEasyPresetActive = filters.difficulty === 'Easy';

  // Toggle preset filters with fixed implementation
  const toggleQuickPreset = () => {
    const isCurrentlyActive = isQuickPresetActive;
    
    setFilters(prev => ({
      ...prev,
      maxTime: isCurrentlyActive ? '' : '30',
    }));
    
    setSearchPerformed(true);
    setShouldFetch(true);
  };

  const toggleEasyPreset = () => {
    const isCurrentlyActive = isEasyPresetActive;
    
    setFilters(prev => ({
      ...prev,
      difficulty: isCurrentlyActive ? '' : 'Easy',
    }));
    
    setSearchPerformed(true);
    setShouldFetch(true);
  };

  const toggleSoloPreset = () => {
    const isCurrentlyActive = isSoloPresetActive;
    
    setFilters(prev => ({
      ...prev,
      players: isCurrentlyActive ? '' : 1,
    }));
    
    setSearchPerformed(true);
    setShouldFetch(true);
  };

  const togglePartyPreset = () => {
    const isCurrentlyActive = isPartyPresetActive;
    
    setFilters(prev => ({
      ...prev,
      difficulty: isCurrentlyActive ? '' : 'Easy',
      players: isCurrentlyActive ? '' : 6,
    }));

    setSearchPerformed(true);
    setShouldFetch(true);
  };

  // Handle search from guided modal
  const handleSearchFromGuidedModal = (searchParams) => {
    // Reset any previous search state
    resetFilters();
    console.log("guided searchpage");
    
    // Set the filters based on guided search
    setFilters(prev => ({
      ...prev,
      players: searchParams.players || '',
      maxTime: searchParams.maxTime || '',
      themes: searchParams.themes || []
    }));
    // Trigger search
    setSearchPerformed(true);
    setShouldFetch(true);
  };

return (
    <div className="bg-gray-900 py-8 text-gray-100">
      <SEO 
        title="Search Board Games"
        description="Search for the perfect board games by player count, time, complexity, theme, and mechanics."
      />
      <div className="max-w-6xl mx-auto px-4">
        
         {/* Search Form */}
        <form onSubmit={handleSearchSubmit}>
          {/* Natural Language Search Bar - Make it HUGE */}
          <div className={`flex flex-col items-center justify-end ${!searchPerformed ? 'h-[240px] mb-4' : 'mb-4 py-3'}`}>
           <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-8 text-blue-400 text-center ${!searchPerformed ? 'mt-[-5vh] sm:mt-[-10vh]' : ''}`}>
              Find the best board games
            </h1>
            
              <div className="grid grid-cols-12 items-center gap-2 sm:gap-4 w-full">
                {/* "I want..." - 2 columns */}
                <div className="col-span-12 sm:col-span-2 text-center sm:text-right">
                  <span className="text-xl sm:text-3xl text-gray-200 font-medium whitespace-nowrap">I want...</span>
                </div>
                
                {/* Search box - 8 columns */}
                <div className="col-span-12 sm:col-span-8 relative">
                  <input
                    type="text"
                    placeholder=""
                    className="w-full p-3 sm:p-5 pl-4 sm:pl-5 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 text-lg sm:text-xl shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={rawQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleNaturalLanguageSearch(value);
                    }}
                  />
                  {!rawQuery && (
                    <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-4 sm:pl-5 text-gray-400 text-lg sm:text-xl">
                      <AnimatedSearchExample />
                    </div>
                  )}
                </div>
                
                {/* Empty space or potential button - 2 columns */}
                <div className="col-span-12 sm:col-span-2">
                  {/* This column is intentionally left empty or could contain a search button */}
                </div>
              </div>
            </div>

              {/* "Not sure where to start?" button */}
              <div className="flex justify-center my-4">
                <button
                  type="button"
                  onClick={() => setIsGuidedModalOpen(true)}
                  className="text-blue-400 hover:text-blue-300 flex items-center gap-2 font-medium"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Not sure where to start?
                </button>
              </div>
        </form>



        {/* Applied Natural Language Filters Indicator */}
        <div className="mb-4 mt-0 flex flex-wrap gap-2 items-center">&nbsp;
          {usingNaturalLanguage && appliedNlFilters && Object.keys(appliedNlFilters).length > 0 && (
            <>
              {(appliedNlFilters.players) && (
                <span className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-xs font-medium">
                  {appliedNlFilters.players} players
                </span>
              )}
              
              {appliedNlFilters.maxTime && (
                <span className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-xs font-medium">
                  Under {appliedNlFilters.maxTime} min
                </span>
              )}
              
              {appliedNlFilters.difficulty && (
                <span className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-xs font-medium">
                  {appliedNlFilters.difficulty} difficulty
                </span>
              )}
              {appliedNlFilters.themes && appliedNlFilters.themes.map(theme => (
                <span key={theme} className="bg-green-900 text-green-100 px-2 py-1 rounded-full text-xs font-medium">
                  {theme}
                </span>
              ))}
              
              {appliedNlFilters.mechanics && appliedNlFilters.mechanics.map(mechanic => (
                <span key={mechanic} className="bg-purple-900 text-purple-100 px-2 py-1 rounded-full text-xs font-medium">
                  {mechanic}
                </span>
              ))}
              
              {searchTerm && (
                <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded-full text-xs font-medium">
                  Search: "{searchTerm}"
                </span>
              )}
            </>
          )}
        </div>

        {/* Filter Buttons - Only show after search */}
        {searchPerformed && (
          <div className="flex flex-col sm:flex-row flex-wrap justify-between mb-4">
            <div className="flex flex-wrap gap-2 mb-2 sm:mb-0">
              {/* Quick Preset Button */}
              <button 
                onClick={toggleQuickPreset}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
                  isQuickPresetActive
                    ? 'bg-blue-600 text-white border-blue-700' 
                    : 'bg-gray-800 text-gray-300 hover:text-gray-100 border-gray-700'
                }`}
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Quick</span>
              </button>

              {/* Easy Preset Button */}
              <button 
                onClick={toggleEasyPreset}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
                  isEasyPresetActive
                    ? 'bg-blue-600 text-white border-blue-700' 
                    : 'bg-gray-800 text-gray-300 hover:text-gray-100 border-gray-700'
                }`}
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Easy</span>
              </button>
            
            {/* Solo Preset Button */}
            <button 
              onClick={toggleSoloPreset}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
                isSoloPresetActive
                  ? 'bg-blue-600 text-white border-blue-700' 
                  : 'bg-gray-800 text-gray-300 hover:text-gray-100 border-gray-700'
              }`}
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Solo</span>
            </button>

            {/* Party Preset Button */}
            <button 
              onClick={togglePartyPreset}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
                isPartyPresetActive
                  ? 'bg-blue-600 text-white border-blue-700' 
                  : 'bg-gray-800 text-gray-300 hover:text-gray-100 border-gray-700'
              }`}
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>Party</span>
            </button>

            {/* Kids Preset Button */}
            <button 
              onClick={() => {
                const isActive = filters.themes.includes("Kids");
                setFilters(prev => ({
                  ...prev,
                  themes: isActive 
                    ? prev.themes.filter(theme => theme !== "Kids") 
                    : [...prev.themes, "Kids"]
                }));
                setSearchPerformed(true);
                setShouldFetch(true);
              }}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg shadow-sm border transition-colors ${
                  isKidsPresetActive
                    ? 'bg-blue-600 text-white border-blue-700' 
                    : 'bg-gray-800 text-gray-300 hover:text-gray-100 border-gray-700'
                }`}
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Kids</span>
            </button>

          </div>
          
 
          {/* Advanced Filters Button - Right Aligned */}
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm border border-gray-700 bg-gray-800 text-gray-300 hover:text-gray-100 transition-colors"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Advanced Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-sm">
                {activeFilterCount}
              </span>
            )}

            {showFilters ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
        </div>
      )}


        {/* Filters Panel - Only show when open AND search performed */}
        {showFilters && searchPerformed && (
          <div className="bg-gray-800 rounded-lg shadow-md border border-gray-700 p-6 mb-6">
            {/* Player Count Sliders */}
            <div className="space-y-2">
              <label className="font-medium">Number of Players: {filters.players || 'Any'}</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      players: ''
                    }));
                    setFiltersChanged(true);
                  }}
                  className={`px-3 py-1 rounded ${filters.players === '' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                >
                  Any
                </button>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={filters.players || 1}
                  onChange={(e) => {
                    const newPlayerCount = parseInt(e.target.value);
                    setFilters(prev => ({
                      ...prev,
                      players: newPlayerCount
                    }));
                    setFiltersChanged(true);
                  }}
                  className={`w-full accent-blue-500 ${filters.players === '' ? 'opacity-50' : ''}`}
                />
              </div>
            </div>


              {/* Other Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select 
                  value={filters.maxTime}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, maxTime: e.target.value }));
                    setFiltersChanged(true);
                  }}
                  className="p-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Max Time (Any)</option>
                  {[30, 60, 90, 120, 180, 240].map(time => (
                    <option key={time} value={time}>≤ {time} min</option>
                  ))}

                  </select>

                <select 
                  value={filters.difficulty}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, difficulty: e.target.value }));
                    setFiltersChanged(true);
                  }}
                  className="p-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Difficulty (Any)</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>

              {/* Multi-select Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium mb-2 text-blue-400">Themes</h3>
                  <div className="border border-gray-700 rounded-lg p-4 h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                    {filtersLoaded ? (
                      filterOptions.theme.map(theme => (
                        <label key={theme} className="flex items-center gap-2 py-1">
                          <input
                            type="checkbox"
                            checked={filters.themes.includes(theme)}
                            onChange={() => {
                              setFilters(prev => ({
                                ...prev,
                                themes: prev.themes.includes(theme)
                                  ? prev.themes.filter(t => t !== theme)
                                  : [...prev.themes, theme]
                              }));
                              setFiltersChanged(true);
                            }}
                            className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
                          />
                          <span className="text-gray-300">{theme}</span>
                        </label>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">Loading themes...</div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-medium mb-2 text-blue-400">Mechanics</h3>
                  <div className="border border-gray-700 rounded-lg p-4 h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                    {filtersLoaded ? (
                       filterOptions.mechanic
                        .filter(mechanic => 
                          // Keep "Auction / Bidding" and filter out all other auction types
                          mechanic === 'Auction / Bidding' || !mechanic.startsWith('Auction')
                        )
                        .map(mechanic => (
                        <label key={mechanic} className="flex items-center gap-2 py-1">
                          <input
                            type="checkbox"
                            checked={filters.mechanics.includes(mechanic)}
                            onChange={() => {
                              setFilters(prev => ({
                                ...prev,
                                mechanics: prev.mechanics.includes(mechanic)
                                  ? prev.mechanics.filter(m => m !== mechanic)
                                  : [...prev.mechanics, mechanic]
                              }));
                              setFiltersChanged(true);
                            }}
                            className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
                          />
                          <span className="text-gray-300">{mechanic}</span>
                        </label>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">Loading mechanics...</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-2">Searching for games...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <EmptyState type="error" onReset={() => {
            setError(null);
            setFiltersChanged(true);
          }} />
        )}

        {/* Results Count */}
        {searchPerformed && !loading && !error && displayedGames.length > 0 && (
          <div className="max-w-6xl mx-auto grid grid-cols-1 px-4">
            <div className="flex justify-between items-center text-sm text-gray-400 mb-4">
              <div>
                Found {games.length} game{games.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        )}

        {/* Game Cards */}
        {searchPerformed && !loading && !error && displayedGames.length > 0 && (
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
            {displayedGames.map(game => (
              <GameCard
                key={game.id}
                game={game}
                onVote={handleVote}
                showLogin={showLogin}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {searchPerformed && !loading && !error && displayedGames.length > 0 && totalPages > 1 && (
          <div className="flex justify-center mt-8 mb-12">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(prev => prev - 1);
                    fetchFilteredGames(currentPage - 1);
                    window.scrollTo(0, 0);
                  }
                }}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1 
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Previous
              </button>
              
              <span className="text-gray-400">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(prev => prev + 1);
                    fetchFilteredGames(currentPage + 1);
                    window.scrollTo(0, 0);
                  }
                }}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages 
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        )}

        {/* No Results */}
        {searchPerformed && !loading && !error && displayedGames.length === 0 && (
          <EmptyState type="no-results" onReset={resetFilters} />
        )}

         {/* Guided Search Modal */}
        <GuidedSearchModal
          isOpen={isGuidedModalOpen}
          onClose={() => setIsGuidedModalOpen(false)}
          onSearch={handleSearchFromGuidedModal}
          filterOptions={filterOptions}
        />
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

    </div>
  );
};

export default SearchPage;